import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { createCorrelationId } from '../utils/correlation-id';
import { environment } from '../../../environments/environment';

const APP_ERROR_TYPE = 'app_error';
const APP_INFO_TYPE = 'app_info';
const HTTP_ERROR_TYPE = 'http_error';
const LOG_ERROR_ENDPOINT = '/api/log-error';
const MARKUP_BODY_PATTERN = /^\s*</;
const HTML_TITLE_PATTERN = /<title[^>]*>([^<]*)<\/title>/i;
const MAX_TITLE_LENGTH = 200;

type LogContext = Record<string, unknown>;

@Injectable({
  providedIn: 'root',
})
export class ErrorLoggerService {
  private readonly auth = inject(Auth, { optional: true });
  private readonly sessionId = createCorrelationId();
  private readonly runtimeConfig = environment;

  logError(error: unknown, context: LogContext = {}): void {
    const payload = {
      severity: 'ERROR',
      userId: this.getUserId(),
      sessionId: this.sessionId,
      correlationId: context['correlationId'] || createCorrelationId(),
      url: window.location.href,
      ...context,
      error: this.normalizeError(error),
    };

    this.write(APP_ERROR_TYPE, payload);
  }

  logHttpError(error: HttpErrorResponse, context: LogContext = {}): void {
    const severity = error.status >= 500 ? 'ERROR' : 'WARNING';
    const payload = {
      severity,
      userId: this.getUserId(),
      sessionId: this.sessionId,
      correlationId: context['correlationId'] || createCorrelationId(),
      url: window.location.href,
      ...context,
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: this.normalizeHttpErrorBody(error.error),
    };

    // A 401 means the token was rejected, and /api/log-error rejects the same token, so
    // reporting it only adds a second 401. AuthService.expireSession handles the cause.
    this.write(HTTP_ERROR_TYPE, payload, error.status !== HttpStatusCode.Unauthorized);
  }

  logInfo(message: string, context: LogContext = {}): void {
    const payload = {
      severity: 'INFO',
      userId: this.getUserId(),
      sessionId: this.sessionId,
      correlationId: context['correlationId'] || createCorrelationId(),
      url: window.location.href,
      ...context,
      error: { message },
    };

    this.write(APP_INFO_TYPE, payload);
  }

  private getUserId(): string | null {
    try {
      return this.auth?.currentUser?.uid || null;
    } catch {
      // Firebase Auth might not be initialized yet during early boot bootstrap errors.
      // Catching ensures the logger itself never throws.
      return null;
    }
  }

  private normalizeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    if (typeof error === 'string') {
      return { message: error };
    }

    if (typeof error === 'object' && error !== null) {
      return { detail: error };
    }

    return { message: 'Unknown error' };
  }

  // 5xx responses from the load balancer or Django are HTML pages. Forwarding the
  // markup makes the Cloud Armor XSS rule reject the report, so the error that most
  // needs logging is dropped. Send a tag-free summary instead.
  private normalizeHttpErrorBody(body: unknown): Record<string, unknown> {
    if (typeof body !== 'string' || !MARKUP_BODY_PATTERN.test(body)) {
      return this.normalizeError(body);
    }

    const title = HTML_TITLE_PATTERN.exec(body)?.[1].trim().slice(0, MAX_TITLE_LENGTH);
    return {
      message: 'Markup response body omitted',
      ...(title && { title }),
      bodyLength: body.length,
    };
  }

  private write(kind: string, payload: Record<string, unknown>, report = true): void {
    // Always log to console in all environments to preserve original behavior and pass tests
    const severity = (payload['severity'] as string) || 'ERROR';
    if (severity === 'INFO' || severity === 'DEBUG') {
      console.info(kind, payload);
    } else {
      console.error(kind, payload);
    }

    if (!report) {
      return;
    }

    // Lazily get the Firebase Auth token if a user is signed in
    let tokenPromise = Promise.resolve<string | null>(null);
    try {
      if (this.auth?.currentUser) {
        tokenPromise = this.auth.currentUser.getIdToken();
      }
    } catch {
      // Firebase might not be initialized yet
    }

    // Fire-and-forget POST request using native fetch to avoid Angular DI circular dependencies
    tokenPromise.then((token) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const baseUrl = (this.runtimeConfig.apiUrl || '').replace(/\/+$/, '');
      const endpointUrl = `${baseUrl}${LOG_ERROR_ENDPOINT}`;

      fetch(endpointUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          ...payload,
          kind,
        }),
      }).catch((err) => {
        // Silent catch to prevent logging loops or crashing the app if the logging endpoint is down
        if (!this.runtimeConfig.production) {
          console.warn('Failed to send log to backend:', err);
        }
      });
    });
  }
}
